import { useEffect, useState } from 'react'
import { PageHeader } from '../../../components/PageHeader'
import { GameTitle } from '../../../components/GameTitle'
import { ChevronRight } from '../../../components/ChevronRight'
import { BackgroundTitle } from '../../../components/BackgroundTitle'
import {
  fetchColorfulWeeklyWinners, colorfulArchiveDateLabel,
  type ColorfulWeeklyWinnerRow,
} from '../api/weekly'
import { readError } from '../../../lib/errors'
import { useT } from '../../../i18n'

interface Props {
  onBack: () => void
  onViewWeek: (eventId: string) => void
}

/**
 * Chronological list of finalized Colorful weeks. Each entry opens
 * that week's crowd-pick results — the per-subject majority colors
 * plus a score leaderboard. The interesting bit for players is
 * seeing which colors the group gravitates toward for each subject.
 */
export function ColorfulArchiveScreen({ onBack, onViewWeek }: Props) {
  const t = useT()
  const [weeks, setWeeks] = useState<ColorfulWeeklyWinnerRow[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    // No week is excluded. The archive is the complete record of
    // settled weeks: if a week was final enough to hand out a trophy,
    // it has to be listed here too, or a player taps a trophy and
    // lands on a week the archive claims doesn't exist. We used to
    // drop `lastWeek` to avoid repeating the Hub's "Last week" card,
    // but that hid the most recent win for a full week. `is_final`
    // already keeps the in-progress week out, which is the real gate.
    fetchColorfulWeeklyWinners()
      .then(rows => { if (!cancelled) setWeeks(rows) })
      .catch(e => { if (!cancelled) setError(readError(e)) })
    return () => { cancelled = true }
  }, [])

  return (
    <div className="screen">
      <BackgroundTitle text={t('Colorful Archive')} color="#74705F" />
      <PageHeader title={t('Colorful · Archive')} onBack={onBack} />

      <div className="flex-1 px-5 py-5 space-y-3 overflow-y-auto pb-6 relative z-10">
        <div className="flex items-center gap-3 mb-2">
          <img src="/games/colorful.webp" alt="Colorful"
               className="w-12 h-12 rounded-2xl object-cover shadow-xs shrink-0" />
          <div>
            <GameTitle game="colorful" size="sm" />
            <p className="text-xs text-brand-muted">
              {t('See what colors the crowd gravitated toward each week')}
            </p>
          </div>
        </div>

        {error && (
          <div className="card p-3 bg-brand-danger/10 border border-brand-danger/30">
            <p className="text-sm text-brand-danger">{error}</p>
          </div>
        )}

        {weeks === null && (
          <>
            <div className="h-16 rounded-2xl bg-brand-elevated animate-pulse" />
            <div className="h-16 rounded-2xl bg-brand-elevated animate-pulse" />
            <div className="h-16 rounded-2xl bg-brand-elevated animate-pulse" />
          </>
        )}

        {weeks?.length === 0 && (
          <p className="text-sm text-brand-muted text-center py-8">
            {t('No finalised weeks yet.')}
          </p>
        )}

        {weeks && weeks.map(w => (
          <button
            key={w.event_id}
            onClick={() => onViewWeek(w.event_id)}
            className="w-full bg-game-colorful-light border-2 border-game-colorful-dark
                       rounded-2xl shadow-card p-4 flex items-center gap-3 text-left
                       active:scale-[0.98] transition-transform"
          >
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-brand-text truncate">
                {colorfulArchiveDateLabel(w.week_start)}
              </p>
              <p className="text-xs text-brand-muted truncate">
                {w.participant_count} player{w.participant_count === 1 ? '' : 's'}
                {' · '}
                {w.winner_names ?? w.author_name}
              </p>
            </div>
            <ChevronRight color="#74705F" />
          </button>
        ))}
      </div>
    </div>
  )
}
